import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { showSuccess, showError, showWarning, showInfo } from '../utils/notifications';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { 
  CreditCard, 
  Download, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  FileText, 
  Filter,
  Loader2,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Star,
  Shield
} from 'lucide-react';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const BillingPayments = ({ user }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Download states
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // Payment methods states
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(null);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(null);

  useEffect(() => {
    if (user && user.email) {
      loadPayments(true); // Initial load
      loadPaymentMethods();
    }
  }, [user]);

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${BACKEND_URL}/api/billing/payment-methods`,
        { headers }
      );
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      // Only show error toast for unexpected errors, not for missing customer setup
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        showError('Failed to load payment methods');
      }
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const setDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      setUpdatingPaymentMethod(paymentMethodId);
      await axios.post(
        `${BACKEND_URL}/api/billing/payment-methods/${paymentMethodId}/set-default`,
        {},
        { headers: getAuthHeaders() }
      );
      
      await loadPaymentMethods(); // Refresh the list
      showSuccess('Default payment method updated successfully');
    } catch (error) {
      console.error('Failed to update default payment method:', error);
      showError('Failed to update default payment method');
    } finally {
      setUpdatingPaymentMethod(null);
    }
  };

  const deletePaymentMethod = async (paymentMethodId) => {
    try {
      setDeletingPaymentMethod(paymentMethodId);
      await axios.delete(
        `${BACKEND_URL}/api/billing/payment-methods/${paymentMethodId}`,
        { headers: getAuthHeaders() }
      );
      
      await loadPaymentMethods(); // Refresh the list
      showSuccess('Payment method removed successfully');
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      showError('Failed to remove payment method');
    } finally {
      setDeletingPaymentMethod(null);
    }
  };

  const addPaymentMethod = async () => {
    try {
      showInfo('Setting up payment method...');
      
      const headers = getAuthHeaders();
      
      // Create a setup intent for adding new payment method
      const response = await axios.post(
        `${BACKEND_URL}/api/billing/setup-intent`,
        {},
        { headers }
      );
      
      const { client_secret } = response.data;
      
      if (!client_secret) {
        throw new Error('No client secret received from server');
      }
      
      // Create a more user-friendly setup flow
      const setupUrl = `https://checkout.stripe.com/c/pay/${client_secret}`;
      
      // Open in new window
      const setupWindow = window.open(
        setupUrl,
        'stripe-setup',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );
      
      if (!setupWindow) {
        showWarning('Please allow popups to add payment methods, or try again.');
        return;
      }
      
      showInfo('Complete the payment setup in the popup window. This page will refresh when you\'re done.');
      
      // Poll for window close and refresh payment methods
      const pollTimer = setInterval(() => {
        if (setupWindow.closed) {
          clearInterval(pollTimer);
          // Refresh payment methods after a short delay to ensure Stripe has processed
          setTimeout(() => {
            loadPaymentMethods();
          }, 2000);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Failed to create setup intent:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 401) {
        showError('Authentication failed. Please refresh the page and try again.');
      } else if (error.response?.status === 400) {
        showError('Unable to set up payment method. Please try again later.');
      } else if (error.response?.data?.detail) {
        showError(`Setup failed: ${error.response.data.detail}`);
      } else {
        showError('Failed to add payment method. Please try again.');
      }
    }
  };

  const loadPayments = async (reset = false) => {
    if (loading || loadingMore) return;
    
    // Early return if no authentication
    const token = localStorage.getItem('token');
    if (!token) {
      setPayments([]);
      setHasMore(false);
      return;
    }
    
    try {
      reset ? setLoading(true) : setLoadingMore(true);
      
      const headers = getAuthHeaders();
      
      const params = new URLSearchParams({
        page: reset ? '1' : page.toString(),
        limit: '20'
      });
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/billing/payments?${params}`,
        { headers }
      );
      
      const newPayments = response.data.payments || [];
      const totalCount = response.data.total_count || 0;
      
      if (reset) {
        setPayments(newPayments);
        setPage(2);
      } else {
        setPayments(prev => [...prev, ...newPayments]);
        setPage(prev => prev + 1);
      }
      
      // Check if there are more payments to load
      setHasMore(payments.length + newPayments.length < totalCount);
      
    } catch (error) {
      console.error('Failed to load payments:', error);
      // Only show error toast for unexpected errors, not for auth/missing customer
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        showError('Failed to load payment history');
      }
      // Set empty data for graceful handling
      setPayments([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
    loadPayments(true);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilter('all');
    setPage(1);
    loadPayments(true);
    setShowFilters(false);
  };

  const downloadInvoice = async (paymentId, invoiceId) => {
    try {
      setDownloadingInvoice(paymentId);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/billing/invoice/${invoiceId}/download`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      showSuccess('Invoice downloaded successfully');
      
    } catch (error) {
      console.error('Failed to download invoice:', error);
      showError('Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'succeeded': { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Paid' },
      'failed': { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Failed' },
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, text: 'Pending' },
      'canceled': { color: 'bg-gray-100 text-gray-800', icon: XCircle, text: 'Canceled' },
      'refunded': { color: 'bg-blue-100 text-blue-800', icon: RefreshCw, text: 'Refunded' }
    };
    
    const config = statusMap[status] || statusMap['pending'];
    const IconComponent = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <IconComponent className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCardBrand = (brand) => {
    const brandMap = {
      'visa': 'Visa',
      'mastercard': 'Mastercard', 
      'amex': 'American Express',
      'discover': 'Discover',
      'diners': 'Diners Club',
      'jcb': 'JCB',
      'unionpay': 'UnionPay'
    };
    return brandMap[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const getCardIcon = (brand) => {
    return <CreditCard className="h-4 w-4" />;
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <Card className="p-8 text-center">
          <CreditCard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Billing & Payments</h2>
          <p className="text-gray-600">
            Please log in to view your billing information and payment history.
          </p>
        </Card>
      </div>
    );
  }

  // Don't load data if user doesn't have proper authentication
  const token = localStorage.getItem('token');
  if (!token) {
    return (
      <div className="space-y-6">
        <Card className="p-8 text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">
            Your session has expired or is invalid. Please refresh the page or log out and log back in.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()} className="bg-purple-600 hover:bg-purple-700">
              Refresh Page
            </Button>
            <Button 
              onClick={() => {
                localStorage.removeItem('token');
                window.location.href = '/';
              }} 
              variant="outline"
            >
              Logout & Login Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Billing & Payments</h2>
          <p className="text-gray-600 mt-1">
            View your payment history and download invoices
          </p>
        </div>
        <Button
          onClick={() => setShowFilters(true)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Filters
        </Button>
      </div>

      {/* Filter Dialog */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Filter Payments</DialogTitle>
            <DialogDescription>
              Filter your payment history by date range and status
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Statuses</option>
                <option value="succeeded">Paid</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="canceled">Canceled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={clearFilters}>
                Clear All
              </Button>
              <Button onClick={applyFilters} className="bg-purple-600 hover:bg-purple-700">
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Methods Section */}
      <Card className="overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
            </div>
            <Button
              onClick={() => setShowPaymentMethods(!showPaymentMethods)}
              variant="outline"
              className="flex items-center gap-2"
            >
              {showPaymentMethods ? 'Hide' : 'Manage'} Methods
            </Button>
          </div>

          {showPaymentMethods && (
            <div className="space-y-4 border-t border-gray-100 pt-6">
              {loadingPaymentMethods ? (
                <div className="text-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-600 mb-2" />
                  <p className="text-gray-600">Loading payment methods...</p>
                </div>
              ) : paymentMethods.length === 0 ? (
                <div className="text-center py-6">
                  <Shield className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-900 mb-2">No payment methods</h4>
                  <p className="text-gray-600 mb-4">Payment methods will be automatically saved when you subscribe to a plan.</p>
                  <Button
                    onClick={addPaymentMethod}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Payment Method
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-4">
                          {getCardIcon(method.card?.brand)}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {formatCardBrand(method.card?.brand)} •••• {method.card?.last4}
                              </span>
                              {method.is_default && (
                                <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                                  <Star className="h-3 w-3" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              Expires {method.card?.exp_month?.toString().padStart(2, '0')}/{method.card?.exp_year}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {!method.is_default && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDefaultPaymentMethod(method.id)}
                              disabled={updatingPaymentMethod === method.id}
                              className="flex items-center gap-1"
                            >
                              {updatingPaymentMethod === method.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Star className="h-3 w-3" />
                              )}
                              Set Default
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deletePaymentMethod(method.id)}
                            disabled={deletingPaymentMethod === method.id || (paymentMethods.length === 1)}
                            className="text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            {deletingPaymentMethod === method.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <Button
                      onClick={addPaymentMethod}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Another Payment Method
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Payment History */}
      <Card className="overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <CreditCard className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600 mb-4" />
              <p className="text-gray-600">Loading payment history...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No payment history</h3>
              <p className="text-gray-600">
                {startDate || endDate || statusFilter !== 'all'
                  ? 'No payments match your current filters.'
                  : 'Your payment history will appear here once you make a subscription payment.'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Plan</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-900">
                                {formatDate(payment.created)}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-green-600" />
                              <span className="font-semibold text-gray-900">
                                {formatAmount(payment.amount, payment.currency)}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm text-gray-900">
                              {payment.description || payment.plan_name || 'Subscription'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="py-4 px-4">
                            {payment.invoice_id ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadInvoice(payment.id, payment.invoice_id)}
                                disabled={downloadingInvoice === payment.id}
                                className="flex items-center gap-2"
                              >
                                {downloadingInvoice === payment.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Download className="h-3 w-3" />
                                )}
                                {downloadingInvoice === payment.id ? 'Downloading...' : 'Download'}
                              </Button>
                            ) : (
                              <span className="text-xs text-gray-400">Not available</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4">
                {payments.map((payment) => (
                  <Card key={payment.id} className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-gray-900">
                          {formatAmount(payment.amount, payment.currency)}
                        </span>
                      </div>
                      {getStatusBadge(payment.status)}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-600">{formatDate(payment.created)}</span>
                      </div>
                      <div className="text-gray-700">
                        Plan: {payment.description || payment.plan_name || 'Subscription'}
                      </div>
                    </div>
                    
                    {payment.invoice_id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoice(payment.id, payment.invoice_id)}
                          disabled={downloadingInvoice === payment.id}
                          className="w-full flex items-center justify-center gap-2"
                        >
                          {downloadingInvoice === payment.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3" />
                          )}
                          {downloadingInvoice === payment.id ? 'Downloading...' : 'Download Invoice'}
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center pt-6">
                  <Button
                    onClick={() => loadPayments(false)}
                    disabled={loadingMore}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {loadingMore ? 'Loading...' : 'Load More Payments'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BillingPayments;